import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import ContactAdminModal from './ContactAdminModal';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData?.password) {
      newErrors.password = "Password is required";
    } else if (formData?.password?.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Helper function to get user-friendly error message
  const getErrorMessage = (error) => {
    if (!error) return 'Login failed. Please try again.';

    const errorMessage = error?.message || '';
    const errorStatus = error?.status;

    // Check for email confirmation error
    if (errorMessage?.toLowerCase()?.includes('email not confirmed') || 
        errorMessage?.toLowerCase()?.includes('email_not_confirmed') ||
        errorStatus === 400 && errorMessage?.toLowerCase()?.includes('confirm')) {
      return 'Please verify your email address before signing in. Check your inbox for the confirmation link.';
    }

    // Check for invalid credentials
    if (errorMessage?.toLowerCase()?.includes('invalid login credentials') ||
        errorMessage?.toLowerCase()?.includes('invalid credentials') ||
        errorMessage?.toLowerCase()?.includes('email or password') ||
        errorStatus === 400) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }

    // Check for user not found
    if (errorMessage?.toLowerCase()?.includes('user not found') ||
        errorMessage?.toLowerCase()?.includes('no user found')) {
      return 'No account found with this email address. Please check your email or contact admin.';
    }

    // Check for too many requests
    if (errorMessage?.toLowerCase()?.includes('too many requests') ||
        errorStatus === 429) {
      return 'Too many login attempts. Please wait a few minutes before trying again.';
    }

    // Return the actual error message if it's meaningful, otherwise generic message
    return errorMessage || 'Login failed. Please try again.';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);

      if (error) {
        // Get the actual error message
        const actualErrorMessage = getErrorMessage(error);
        setErrors({ general: actualErrorMessage });
        setIsLoading(false);
        return;
      }

      // Check if student is restricted
      if (data?.profile?.role === 'student') {
        // Fetch student profile to check restriction status
        const { supabase } = await import('../../../lib/supabase');
        const { data: studentProfile } = await supabase
          ?.from('student_profiles')
          ?.select('is_restricted, approval_status')
          ?.eq('user_id', data?.user?.id)
          ?.maybeSingle();

        if (studentProfile?.is_restricted === true) {
          navigate('/pending-approval', { state: { restricted: true } });
          return;
        }

        if (studentProfile?.approval_status === 'pending') {
          navigate('/pending-approval');
          return;
        }

        navigate('/student-dashboard');
        return;
      }

      // Check user role and redirect accordingly
      if (data?.profile?.role === 'institute') {
        navigate('/institute-dashboard');
      } else if (data?.profile?.role === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/student-dashboard');
      }
    } catch (err) {
      const actualErrorMessage = getErrorMessage(err);
      setErrors({ general: actualErrorMessage });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6">
      {errors?.general && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors?.general}</p>
        </div>
      )}
      <Input
        label="Email"
        type="email"
        name="email"
        placeholder="institute@example.com"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
      />
      <div className="relative">
        <Input
          label="Password"
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Enter your password"
          value={formData?.password}
          onChange={handleChange}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
        >
          <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
        </button>
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e?.target?.checked)}
            className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
          />
          <span className="text-sm text-foreground">Remember me</span>
        </label>

        <button
          type="button"
          onClick={() => navigate('/institute-login')}
          className="text-sm text-primary hover:text-primary/80 transition-smooth font-medium"
        >
          Forgot password?
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right"
      >
        {isLoading ? 'Signing in...' : 'Sign In'}
      </Button>
      <div className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <button
          type="button"
          onClick={() => setShowContactModal(true)}
          className="text-primary hover:text-primary/80 transition-smooth font-medium underline underline-offset-2"
        >
          Contact Admin
        </button>
      </div>
      <ContactAdminModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
    </form>
  );
};

export default LoginForm;